import { compile, Renderer } from "micromustache";
import { Injectable } from "../../dependencies/injectable";
import { Injected } from "../../dependencies/injected";
import { PropertyConfig } from "../../forms/propertyConfig";
import { ValidationError } from "../../forms/validationError";
import { anyObject, getProp } from "../../tools/any.tools";
import { APP_SERVICE, AppService } from "../app.service";
import { LOG_SERVICE, LogService } from "../log.service";
import { VALIDATION_SERVICE, ValidationService } from "../validation.service";
import { ERROR_MESSAGES, FALLBACK_ERROR_MESSAGE } from "./validation.i18n";
import { Validator } from "./validators/validator";

/**
 * Builds a DefaultValidationService
 */
export function buildDefaultValidationService(): ValidationService {
    const validationService = new DefaultValidationService();
    return validationService;
}

@Injectable(VALIDATION_SERVICE)
export class DefaultValidationService implements ValidationService {

    @Injected(LOG_SERVICE)
    protected _log!: LogService;

    @Injected(APP_SERVICE)
    protected _app!: AppService;

    /**
     * Language to use if the current application language is not handled
     */
    protected _fallBackLang = "fr";

    /**
     * Record of all registered validators by name
     */
    private _validators: Record<string, Validator> = {};

    /**
     * Cache of micromustache error messages by lang-validator
     */
    private _microMustacheMessagesCache: Record<string, Renderer> = {};

    /**
     * Add/Set Validator within validators record 
     * If a validator with the same name is already present, 
     * - it will be overridden only if params.overrideIfAlreadyPresent is true
     * - a message will be output to console except if params.overrideIfAlreadyPresent is true or params.doNotDisplayErrorIfAlreadyPresent is true
     * @returns true if validator was added
     */
    public addValidator(validatorName: string, validator: Validator, params?: { overrideIfAlreadyPresent?: boolean, doNotDisplayErrorIfAlreadyPresent?: boolean }): boolean {
        if (!validatorName) {
            this._log.error("Validator name not identified for", validator, ", addValidator was skipped");
            return false;
        }

        if (this._validators[validatorName]) {
            if (!params?.overrideIfAlreadyPresent && !params?.doNotDisplayErrorIfAlreadyPresent) {
                this._log.error("A " + validatorName + " validator is already registered, addValidator was skipped");
                return false;
            }
            if (!params?.overrideIfAlreadyPresent) {
                // no error message but no override asked
                return false;
            }
        }
        this._validators[validatorName] = validator;
        return true;
    }

    /**
     * Check if the property values satisfy validator constraints
     * Returns a ValidationError for all properties that are not valid
     * Returns undefined if value is validated
     * @param allProperties
     * @param allPropertiesValues
     * @param propertiesToValidate list of props to validate, if undefined given, validate all props
     */
    public validate(allProperties: Record<string, PropertyConfig>, allPropertiesValues: Record<string, unknown>, propertiesToValidate?: string[]): Record<string, ValidationError> {
        const lang = getProp<anyObject>(ERROR_MESSAGES, this._app.lang) ? this._app.lang : this._fallBackLang;

        const errors: Record<string, ValidationError> = {};
        for (const propertyUid in allProperties) {
            // If propertiesToValidate is given and not empty, then do not checks errors for propertyUid if not included
            if (propertiesToValidate?.length && !propertiesToValidate.includes(propertyUid)) {
                continue;
            }

            const prop = allProperties[propertyUid];
            if (!prop?.validators) { continue; }

            // if property has validators, we check the value associated to this property is valid
            const propertyValue = allPropertiesValues[propertyUid];

            // Sort validators to evaluate error validators before warning validators
            const validators: { validator: Validator, params: unknown, name: string }[] = [];
            for (const validatorName in prop.validators) {
                const validator = this._validators[validatorName];
                const validatorParams = prop.validators[validatorName];
                // Build object with all info, to simplify loop
                const validatorWithParams = { validator, params: validatorParams, name: validatorName };

                // Useless to evaluate validator, not found
                // if validatorParams is set to false, the validator is not active
                if (!validator || validatorParams === false) { continue; }

                if (validator.isWarning) {
                    // Add warning validator at end of array to evaluate last
                    validators.push(validatorWithParams);
                } else {
                    // Add error validator at begin of array to evaluate first
                    validators.unshift(validatorWithParams);
                }
            }

            for (const validatorWithParam of validators) {
                const validator = validatorWithParam.validator;
                const validatorParams = validatorWithParam.params;
                const validatorName = validatorWithParam.name;

                const isValid = validator.isValid(prop, propertyValue, validatorParams, allProperties, allPropertiesValues);
                if (isValid) { continue; }

                let error: ValidationError;
                if (validator.getError) {
                    // validator generates custom error messages
                    error = validator.getError(lang, prop, propertyValue, validatorParams, allProperties, allPropertiesValues);
                    if (error.isWarning === undefined) { error.isWarning = validator.isWarning; }
                } else {
                    // validator has a standard message relying on micromustache

                    // get micromustache template from cache or build it
                    const cacheKey = lang + "-" + validatorName;
                    let errorTemplate = this._microMustacheMessagesCache[cacheKey];
                    if (cacheKey && !errorTemplate) {
                        const errorMessage = getProp<anyObject>(ERROR_MESSAGES, lang)[validatorName] ?? getProp<anyObject>(ERROR_MESSAGES, lang)[FALLBACK_ERROR_MESSAGE] ?? "Validation i18n error";
                        this._microMustacheMessagesCache[cacheKey] = compile(errorMessage);
                        errorTemplate = this._microMustacheMessagesCache[cacheKey];
                    }

                    // build error message
                    const errorMessageData = {
                        propLabel: prop.label,
                        validatorParams
                    };
                    error = { errorMessage: errorTemplate.render(errorMessageData), isWarning: validator.isWarning, validatorName };

                }
                if (error) {
                    errors[propertyUid] = error;
                }
                // we stop on the first invalid validator for one property
                break;

            }
        }

        return errors;
    }

    /**
     * @returns a default ValidationError
     */
    public getDefaultValidationError(propertyLabel: string, isWarning?: boolean): ValidationError {
        const lang = getProp<anyObject>(ERROR_MESSAGES, this._app.lang) ? this._app.lang : this._fallBackLang;
        const cacheKey = lang + "@FALLBACK_ERROR_MESSAGE";
        let errorTemplate = this._microMustacheMessagesCache[cacheKey];
        if (cacheKey && !errorTemplate) {
            const errorMessage = getProp<anyObject>(ERROR_MESSAGES, lang)[FALLBACK_ERROR_MESSAGE] ?? "Validation i18n error";
            this._microMustacheMessagesCache[cacheKey] = compile(errorMessage);
            errorTemplate = this._microMustacheMessagesCache[cacheKey];
        }

        // build error message
        const errorMessageData = {
            propLabel: propertyLabel
        };
        return { errorMessage: errorTemplate.render(errorMessageData), isWarning };
    }
}